/**
 * Talking to the API.
 *
 * Same origin, so `credentials: 'same-origin'` is enough and there is no CORS
 * preflight on anything. The session travels as an HttpOnly cookie the browser
 * attaches itself — nothing here reads or stores a token, which is the point of
 * it being HttpOnly.
 */

import type {
  CurriculumNode,
  ExtractionFailure,
  ObjectiveStatus,
  PlanSource,
  ReachWindow,
  ResearchProgress,
} from '@nabu/shared';

export type { ResearchProgress, ResearchRun, ResearchSubject } from '@nabu/shared';

/*
 * Re-exported so that a screen needs one import for the call and its answer. The declaration
 * lives in `plan/coverage.ts` with the rules about what may be said about it — see that file.
 */
import type { CoverageView } from './plan/coverage';
export type { CoverageView } from './plan/coverage';

/**
 * A curriculum node as it comes back today: the shared shape, plus the subject it was built
 * from.
 *
 * **Declared here rather than waiting for `CurriculumNode` to carry it**, because the attribute
 * is written by the pass that is being built beside this one and the browser holds a plain JSON
 * row either way — an optional field in an intersection is the same type whether or not the
 * shared declaration catches up. What must not happen while it does is a plan screen that
 * refuses to compile, or one that assumes the field is always there: every node of the two
 * objectives being taught from today has no `subjectId` at all, and the screens have to render
 * those without a word about subjects rather than with "unknown" on every row.
 */
export type PlanNode = CurriculumNode & { subjectId?: string };

/**
 * The three answers to "where does the material come from", as the browser holds them.
 *
 * Named rather than repeated at the four places that used to spell out the union, because F-119
 * added the third value and the compiler finding every one of them is how this file's own
 * comment on `planFrom` claimed the type would earn its keep. `PlanSource` in `@nabu/shared` is
 * the same union; this is an alias, so the two cannot drift, and the alias exists only so that
 * the browser's own vocabulary reads as the browser's.
 */
export type PlanSourceValue = PlanSource;

export interface ApiError {
  error: string;
  status: number;
  /**
   * Which of several things was wrong, when the error code alone does not say.
   *
   * The reach window is the case that needs it: one `invalid_window` covers a cap
   * out of range, an unreadable time, an empty window and one that wraps past
   * midnight, and a form of three controls cannot be corrected from the word
   * "invalid". Absent on every refusal that is already specific.
   */
  reason?: string;
  /**
   * The ceiling a refused upload went over, so the message can name the number.
   *
   * "Too large" without a limit is not a correction: a learner cannot tell
   * whether to retake the photograph or split the PDF, and the two limits here
   * differ by more than a factor of two.
   */
  maxBytes?: number;
}

async function post<T>(path: string, body: unknown): Promise<T | ApiError> {
  const response = await fetch(path, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });

  // Parsed before the status is checked, because the API returns its reasons in
  // the body — a bare status would lose "rate_limited" versus "invalid_number",
  // which are different things to tell somebody.
  const text = await response.text();
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    return { error: 'unreadable_response', status: response.status };
  }

  if (!response.ok) {
    const failure = parsed as { error?: string; reason?: string; maxBytes?: number };
    return {
      error: failure.error ?? 'request_failed',
      status: response.status,
      ...(failure.reason ? { reason: failure.reason } : {}),
      ...(typeof failure.maxBytes === 'number' ? { maxBytes: failure.maxBytes } : {}),
    };
  }
  return parsed as T;
}

export function isError(value: unknown): value is ApiError {
  return typeof value === 'object' && value !== null && 'error' in value && 'status' in value;
}

/*
 * Declared in `session.ts` and re-exported here, so a screen still has one import for the
 * call and its answer while the rules that read it stay testable without a browser.
 */
import type { Session } from './session';
export type { Session };

export async function whoAmI(): Promise<Session> {
  try {
    const response = await fetch('/api/auth/me', { credentials: 'same-origin' });
    if (!response.ok) return { signedIn: false };
    return (await response.json()) as Session;
  } catch {
    // Offline, or the API is down. Not signed in is the safe answer and the app
    // still renders — a landing page must not blank out because a fetch failed.
    return { signedIn: false };
  }
}

export const signOut = () => post<{ ok: true }>('/api/auth/signout', {});

/*
 * Signing in (F-101), and since F-103 the only way.
 *
 * `startSignIn` and `verifyCode` posted a WhatsApp number at `/api/auth/start` and
 * `/api/auth/verify`. Both routes are gone with the sidecar: nothing can deliver a code to
 * a number any more, and a client that kept calling them would have been told `200 { ok:
 * true }` — the API may not disclose whether an identity is registered — while nobody was
 * sent anything.
 *
 * Three calls rather than two because attaching an address to an account that already
 * exists is a different act from being admitted by one — `linkEmail` is the only one
 * that needs a session, and the API will refuse it without one.
 */

/**
 * Asks for a code by email.
 *
 * The locale goes with it, unlike the phone path: the message is written by the API and
 * this is the first thing a new learner ever receives from us, so it should be in the
 * language they are reading the site in. `document.documentElement.lang` is what the
 * provider above writes, so it is the same answer the page is rendering with.
 */
export const startEmailSignIn = (email: string) =>
  post<{ ok: true }>('/api/auth/email/start', {
    email,
    locale: document.documentElement.lang || 'es',
  });

export const verifyEmailCode = (email: string, code: string) =>
  post<{ ok: true; isNew: boolean; learner: { name: string; locale: string } }>(
    '/api/auth/email/verify',
    {
      email,
      code,
      locale: document.documentElement.lang || 'es',
      // As on the phone path: only the browser knows this, and a schedule without it is
      // meaningless.
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    },
  );

/**
 * Attaches a proved address to the account already signed in.
 *
 * No locale and no timezone: this learner exists and already has both, and sending them
 * again would let a second sign-in quietly overwrite a schedule they chose.
 */
export const linkEmail = (email: string, code: string) =>
  post<{ ok: true; email: string }>('/api/auth/email/link', { email, code });

// ---- Carrying this session to another device (F-157) ----

/**
 * A link that signs this learner in somewhere else, and lands them where they are now.
 *
 * **This is the seam the install and onboarding screens call.** A QR code on a desktop, or a
 * tap that carries a browser session into a home-screen app the learner has just added, puts
 * `url` in front of them and nothing else has to know how a hand-off is addressed — the API
 * builds the whole URL, so there is exactly one place that decides its shape.
 *
 * **What comes back is a bearer credential.** Anybody who reads it is signed in as this
 * learner for as long as it lasts, which is why it is fetched at the moment it is shown rather
 * than held anywhere: do not put it in `localStorage`, do not keep it in a component that
 * survives the screen, and do not re-render it into a page that stays open. It is single use
 * and it expires — `expiresAt` and `expiresInSeconds` are both returned so a screen can say
 * so, and say it from the number the API actually enforces rather than from a constant of its
 * own.
 *
 * `path` and `objectiveId` are where to land. Pass what `sectionHref` would — that function
 * already holds the rule about which screens are about one plan and which are not — and the
 * API resolves the pair against the route table before it stores it, so an address the app
 * has since renamed still works and an address that is not ours is refused rather than
 * redirected to.
 */
export const createHandoffLink = (path: string, objectiveId?: string) =>
  post<{
    url: string;
    destination: string;
    expiresAt: string;
    expiresInSeconds: number;
  }>('/api/auth/handoff', { path, ...(objectiveId ? { objectiveId } : {}) });

// ---- Mail: is it arriving, and do they want the part they can stop (F-106) ----

/**
 * What the API knows about mail to this learner's address.
 *
 * `suppressed` is a fact about the mailbox — a hard bounce or a complaint — and the two
 * opt-outs are choices they made. They are separate fields because they are separate
 * things: clearing a bounce must not re-subscribe somebody who unsubscribed, and
 * unsubscribing must not look like a delivery problem.
 *
 * **`reportsOptOut` and `nudgeOptOut` are two switches and not one, which is F-124.** The
 * nudge is the mail sent when a question is waiting and no device can be notified, so for
 * a learner who has not installed the app it is the only thing that reaches them; sharing
 * a switch with end-of-day summaries meant turning off the second silently turned off the
 * first.
 */
export interface MailState {
  /** Absent when no address is attached. There is nothing to report then. */
  email?: string;
  suppressed: boolean;
  reason?: 'hard_bounce' | 'complaint';
  /** ISO 8601, when it was recorded. */
  at?: string;
  /** The provider's own words. "550 5.1.1 user unknown" is actionable; "bouncing" is not. */
  diagnostic?: string;
  canRetry: boolean;
  reportsOptOut: boolean;
  nudgeOptOut: boolean;
  /**
   * Devices on the account that could be notified instead — the whole account, not this
   * browser.
   *
   * It is what makes the sentence beside the nudge switch true or false: zero devices and
   * no nudge means nothing reaches this learner until they open the app themselves. The
   * browser cannot answer it, because `pushManager.getSubscription()` speaks only for the
   * device in front of them and somebody may have a phone they are not holding.
   */
  pushDevices: number;
}

export const getMailState = () => get<MailState>('/api/account/mail');

/**
 * Saves the switches a screen actually changed, and never the ones it did not.
 *
 * A partial body on purpose (F-124): the API leaves any category the request does not name
 * exactly where it was. Sending the whole object would mean a screen that knows about one
 * switch speaking for both — and the switch it would speak for by accident is the one that
 * decides whether an uninstalled learner can be reached at all.
 */
export const setMailPreferences = (wanted: {
  reportsOptOut?: boolean;
  nudgeOptOut?: boolean;
}) => post<MailState>('/api/account/mail', wanted);

/**
 * Asks for the suppression to be lifted, which spends one bounce finding out.
 *
 * Its own call rather than part of the save, because the two are not alike: the preference
 * is free and reversible, and this one sends real mail to an address that last time refused
 * it. 409 with a `reason` when the state refuses — no address, nothing wrong, or too soon
 * after the last attempt.
 */
export const retryMail = () => post<MailState>('/api/account/mail/retry', {});

/**
 * Stops the unsubscribable mail, with no session and nothing but the token from the link.
 *
 * **Always answers 200, valid token or not**, so nobody can learn from it whether an
 * address is one of ours. That means the page cannot report failure and should not pretend
 * to: it says the request was recorded, which is true either way.
 */
export const unsubscribeByToken = (token: string) =>
  post<{ ok: true }>(`/api/mail/unsubscribe?t=${encodeURIComponent(token)}`, {});

// ---- Objectives ----

export interface Objective {
  objectiveId: string;
  title: Record<string, string>;
  description: string;
  target: { mode: 'score'; targetScore: number } | { mode: 'date'; targetDate: string };
  // The union comes from the shared domain rather than being restated here, so
  // adding a status server-side breaks the `obj.status.*` copy at compile time
  // instead of rendering a raw identifier like `researching` at a learner.
  status: ObjectiveStatus;
  createdAt: string;
  /**
   * How this objective's plan is built (F-118), or absent on every objective made before the
   * choice existed — which means research, and is what `isUploadsOnly` in `plan/step.ts` decides.
   *
   * Typed as the exact values rather than as a bare string so that one added server-side breaks
   * the screens that branch on it, which is the same argument the comment on `status` makes: an
   * unrecognised value rendered at a learner, or silently treated as the default, is the one
   * outcome a mode chosen on purpose cannot survive. F-119's `scoped` is the case that proved
   * it — adding it here is what listed every screen that had to learn about it.
   */
  planFrom?: PlanSourceValue;
  /**
   * How many messages are waiting in this plan's chat (F-151 step 8).
   *
   * Answered by `GET /api/objectives/current` only, counted against **this plan's own** read
   * marker — which is what step 8 moved off the learner's row so that a per-tab number could
   * be true. `GET /api/objectives` does not carry it: the history is closed plans, and a count
   * beside one is a number nobody can clear.
   *
   * **Absent is not nought**, and the tabs draw nothing for absent. It means the count could
   * not be taken, or that the API answering is deployed from before this step; nought is the
   * claim that there is nothing waiting.
   */
  unread?: number;
  /** Absent on objectives closed before the field existed, and on live ones. */
  closedAt?: string;
  previousStatus?: ObjectiveStatus;
  /**
   * Why the last research pass did not finish, when it did not.
   *
   * A failed pass puts the objective back to `drafting`, which on its own is
   * indistinguishable from never having started one — the screen would offer the
   * button again as though nothing had happened, and a learner who pressed it
   * twice would be told twice that it takes fifteen minutes without ever being
   * told what went wrong.
   *
   * This carries *only* failures. A successful pass writes its summary to
   * `researchDetail` and clears this, so the screen never has to work out which
   * of the two it is holding.
   */
  researchError?: string;
  /** What a finished research pass produced. Never a failure. */
  researchDetail?: string;
  /**
   * Why the last curriculum build did not finish, when it did not.
   *
   * A separate field from the success summary, and that is load-bearing here in a
   * way it is not for research: a failed synthesis returns the objective to
   * `review`, which is exactly where a *successful* one leaves it too. There is no
   * status that tells the two apart, so the field has to.
   */
  synthesisError?: string;
  /** What a finished synthesis built — "44 nodes from 1 documents". Never a failure. */
  synthesisDetail?: string;
  /**
   * Which generation of nodes the objective is holding, once it holds any.
   *
   * Read here for one thing: whether a plan exists for an upload to be folded into.
   * It is the same condition `planAutoFold` in `packages/ingest/src/extract.ts` gates
   * the automatic fold on, so a screen that promised it on any other test would
   * promise something the pipeline refuses.
   */
  curriculumVersion?: string;
  /**
   * What the last successful pass added and reworded, as numbers.
   *
   * **The screen has to state one outcome and the two detail fields are prose.** An
   * upload now folds itself in and the fold writes its own questions, so what a
   * learner is owed at the end is a single sentence — "3 new topics, 12 new
   * questions" — and building it out of `synthesisDetail` and `itemsDetail` would mean
   * parsing integers back off strings two pipelines wrote for a log. These are the
   * same integers, written by the pass that knows them.
   *
   * Absent on an objective whose last pass predates F-92, and absent after a failure,
   * which is why every read of them is guarded rather than defaulted to zero: "0 new
   * topics" and "we do not know" are different things to show somebody.
   */
  addedNodes?: number;
  changedNodes?: number;
  /**
   * Present once a complete set of questions exists, naming which set.
   *
   * This is the only signal that generation finished, because generation
   * deliberately never moves the status — see `startItemGeneration`. A run that
   * failed leaves no version behind, which is what lets the next run clean up after
   * it.
   */
  itemVersion?: string;
  /** What generation produced — "180 items across 45 nodes". Never a failure. */
  itemsDetail?: string;
  /**
   * How many questions the last successful generation wrote. See `addedNodes`.
   *
   * Zero is a real answer and the common one after a fold that changed nothing: every
   * node was already current, so no question needed writing.
   *
   * **Present only while the last thing generation wrote was a finished run.** It is
   * removed on a progress write and on a failure, which is what makes "present" answer
   * "has the run finished" — generation never moves the status, so there is nothing
   * else on the row that can. See `recordOutcome` in `packages/ingest/src/items.ts`.
   */
  itemsWritten?: number;
  /**
   * When generation last wrote anything, finished or not.
   *
   * Read for one purpose: telling this run's outcome from the previous one's. A fold
   * does not touch the generation fields, so the screen has to remember where they
   * were when the fold finished and watch this move off it.
   */
  itemsAt?: string;
  /** Why generation did not finish. Never a count. */
  itemsError?: string;
  /**
   * The curriculum version a generation was started for, while it is still running.
   *
   * **The only thing on the row that knows questions are being written**, and until it existed
   * nothing did: generation deliberately takes no status and no lock, so a run in flight was
   * indistinguishable from one that had never been asked for, and the panel offered a primary
   * button to start what was already going. Written by the API when the learner presses and by
   * `synthesise.ts` when a fold starts one on its own; removed by `recordOutcome` on both
   * endings.
   *
   * It names a version rather than saying `true` because a fold mints a new one: a marker for
   * the previous curriculum is about questions for a plan that is gone. `plan/questions.ts`
   * compares it against `curriculumVersion` and owns every judgement made from it.
   */
  itemsStartedFor?: string;
  /**
   * When that generation was started, so a run that died cannot hide the button for ever.
   *
   * Unlike `researching` and `synthesising`, this one is **not** taken off the row by the API
   * when it goes stale — the staleness rule is `ITEMS_RUN_MS` in `plan/questions.ts` and is
   * derived from the item pipeline's own pass budget rather than from `STALE_LOCK_MS`, so it
   * lives with the screen that uses it instead of being a second number inside `withLiveLocks`.
   */
  itemsStartedAt?: string;
  /**
   * What the objective asked for that the sources did not carry.
   *
   * Short phrases in the learner's own language, written by synthesis alongside
   * the tree. **Not an error, and not a list of things nabu got wrong** — it is
   * the one piece of a research document's provenance that a learner can act on,
   * and it exists as data precisely so it never becomes a question again. A
   * learner was once asked in earnest whether the researcher had managed to open
   * a WHO document; see D-30 and D-31.
   *
   * Declared here rather than on the shared `Objective`, like every other field
   * a pipeline writes onto the row — the API returns the stored item, so a new
   * attribute arrives without anything in between having to be widened.
   */
  gaps?: string[];
  /**
   * A *research* pass is running on an objective that already has a plan.
   *
   * The same shape as `synthesising` and present for the same reason — the status
   * must not move, at `active` because it stops the learner's questions and at
   * `review` because a failed pass would strand the curriculum on `drafting`. The
   * API takes this attribute off the row it hands over once the lock is older than
   * any pass could be, so `true` here means a pass that is still plausibly running
   * rather than debris from one that was killed.
   */
  researching?: boolean;
  /**
   * A synthesis pass is running *on an objective that is still sending*.
   *
   * **The status cannot say this, and that is the whole point of the attribute.**
   * A running objective must stay `active` at every instant or the due sweep stops
   * finding the learner and the questions stop — so a pass folding new material in
   * leaves the status alone and sets this instead. It is therefore the only thing
   * on the screen that knows a fold is in flight, and it is what the fold panel
   * watches rather than the status.
   *
   * Written by the API when the pass is started and removed by the pipeline on both
   * success and failure. Absent means nothing is running.
   */
  synthesising?: boolean;
}

/**
 * The statuses an objective can be brought back from.
 *
 * `shelved` sits alongside `done` and `abandoned` because all three are equally
 * finished as far as the screen is concerned — but it is emphatically not a
 * synonym for `abandoned`, and the copy for the three is written separately. See
 * the note on `Objective.status` in the shared domain.
 */
const CLOSED_STATUSES: readonly ObjectiveStatus[] = ['done', 'abandoned', 'shelved'];

export const isClosed = (objective: Objective) => CLOSED_STATUSES.includes(objective.status);

export async function listObjectives(): Promise<{ objectives: Objective[] } | ApiError> {
  try {
    const response = await fetch('/api/objectives', { credentials: 'same-origin' });
    const parsed = (await response.json()) as unknown;
    if (!response.ok) {
      return { error: (parsed as { error?: string }).error ?? 'request_failed', status: response.status };
    }
    return parsed as { objectives: Objective[] };
  } catch {
    return { error: 'unreachable', status: 0 };
  }
}

/**
 * States an objective, including how its plan is to be built.
 *
 * `planFrom` is sent on every create, including when it is the default. The server would treat
 * an absent value as `research` — that is what every objective made before F-118 means — but a
 * row that says nothing is indistinguishable from one made before the question existed, and the
 * first thing anybody asks of a plan that researched when its owner says they chose otherwise is
 * what the row says.
 */
export const createObjective = (
  description: string,
  target: Objective['target'],
  planFrom: PlanSourceValue,
) => post<CreatedObjective>('/api/objectives', { description, target, planFrom });

/**
 * What stating an objective answers with, which is now two facts and not one. F-141.
 *
 * Leo: *"the user should not have to click on anything for this to happen."* So the create also
 * starts the research pass, and the answer has to say whether it started — because the two
 * outcomes want opposite screens. Started, the objective comes back `researching` and the wait
 * begins with no button in it. Not started, it is still `drafting`, the plan screen correctly
 * offers *start research* as the way back, and the learner is owed the reason.
 *
 * `not_applicable` is the third answer and is not a failure: an objective whose plan is built from
 * the learner's own pages has no pass to start, and its research — if it has any — begins when the
 * first page has been read. Reporting that as "research did not start" would put an error under an
 * objective that is behaving exactly as its owner asked.
 */
export interface CreatedObjective {
  objective: Objective;
  research: 'started' | 'not_started' | 'not_applicable';
  /** Why it did not start, in the API's own vocabulary. Present only with `not_started`. */
  reason?: string;
}

/**
 * Changes how an existing objective's plan is built.
 *
 * **It starts nothing.** Switching to `research` makes a pass possible and the plan screen then
 * offers it; a setting that quietly spent the most expensive call in the system would be the one
 * button this app has always refused to build. Switching to `uploads` closes that door for every
 * future press, because the row itself is what the three research entry points read.
 *
 * Refused with `busy` while a pass is running, which is not a failure to report as one: the
 * choice is still available a minute later and the alternative — changing it under a research
 * pass in flight — is a bill for the thing the learner just opted out of.
 */
export const updatePlanSource = (objectiveId: string, planFrom: PlanSourceValue) =>
  post<{ planFrom: PlanSourceValue }>('/api/objectives/plan-source', {
    objectiveId,
    planFrom,
  });

/**
 * The plans being worked on.
 *
 * `objective` is the one an unqualified request is about — the plan most recently asked from
 * — and is what every screen here reads today. `objectives` is all of them, added by F-151
 * step 6 so the tabs have it without another route; it is optional because a browser holding
 * this code may be talking to an API deployed before it, and a missing list must read as
 * "this deploy does not say" rather than as "no plans".
 *
 * At today's slot cap of one the list holds exactly `objective`, or is empty when it is null.
 */
export async function currentObjective(): Promise<
  { objective: Objective | null; objectives?: Objective[] } | ApiError
> {
  try {
    const response = await fetch('/api/objectives/current', { credentials: 'same-origin' });
    const parsed = (await response.json()) as unknown;
    if (!response.ok) {
      return { error: (parsed as { error?: string }).error ?? 'request_failed', status: response.status };
    }
    return parsed as { objective: Objective | null; objectives?: Objective[] };
  } catch {
    return { error: 'unreachable', status: 0 };
  }
}

/** Changes the target alone. The description is settled once and never resent. */
export const updateTarget = (objectiveId: string, target: Objective['target']) =>
  post<{ target: Objective['target'] }>('/api/objectives/target', { objectiveId, target });

export const closeObjective = (objectiveId: string, status: 'done' | 'abandoned') =>
  post<{ ok: true }>('/api/objectives/close', { objectiveId, status });

/**
 * Brings a closed objective back, whether or not one is already live.
 *
 * One call for both cases deliberately: the browser does not decide whether
 * something has to be set aside first. `status` is where the objective landed —
 * it resumes what it was doing rather than restarting as a draft — and `shelved`
 * names the objectives that were moved out of the way, **absent rather than empty** when
 * none were (F-151 step 6): the cap is the learner's tier's now, so a raised cap resumes a
 * plan with nothing set aside, and `[]` would read as truthy to the screen that says which
 * of the two happened.
 *
 * `shelvedMidRun` is the subset of those that had a pass running, and it is the difference
 * between a screen that is quiet about six dollars of work and one that says where it went.
 * Absent rather than empty for the same reason. The run is not cancelled — see
 * `shelveForRoom` in the API for where each half of that was checked.
 *
 * **It carries which pass, not only which plan, because the true sentence differs.** Research
 * ends in a notification the learner will actually receive; a plan build ends in nothing sent
 * anywhere, because there is no `curriculum_ready` event. One message for both would promise
 * one of them a notice that never arrives.
 */
export const reactivateObjective = (objectiveId: string) =>
  post<{
    ok: true;
    objectiveId: string;
    status: ObjectiveStatus;
    shelved?: string[];
    shelvedMidRun?: { objectiveId: string; pass: 'research' | 'build' }[];
  }>('/api/objectives/reactivate', { objectiveId });

/**
 * Asks for the research pass. Answers 202, not 200.
 *
 * "Accepted and running", because the pass takes minutes and an HTTP request that
 * waited for it would be cut off at the edge long before it finished. The
 * returned status is what the objective is *now*; where it got to afterwards is
 * only ever read back from the objective itself.
 *
 * Valid from `drafting` alone, and the API refuses a second start with
 * `not_drafting` rather than starting one — the status change is the lock. That
 * refusal is the backstop, not the plan: research is the most expensive call the
 * system makes, so the screen must not offer a second start in the first place.
 */
export const startResearch = (objectiveId: string) =>
  post<{ ok: true; status: 'researching' }>('/api/objectives/research', { objectiveId });

/**
 * Asks research to go looking again, on an objective that already has a plan.
 *
 * **The most expensive thing a learner can press: $2.38 a pass, measured
 * (docs/costs.md).** Which is why it is a different call from `startResearch`
 * rather than the same one relaxed — that one is valid from `drafting` only and its
 * status change is its lock, and neither property survives being widened to an
 * objective that is running.
 *
 * The findings are *added* to the material already there: research writes each run
 * under its own prefix in the content store, and the fold that follows extends the
 * plan rather than replacing it, so node ids — and the review history hanging off
 * them — survive. Nothing about the plan changes until the learner asks for the
 * fold.
 *
 * No `status` comes back, because the objective's does not move. `researching` on
 * the objective is what says a pass is in flight. `already_running` is a refusal to
 * pay twice; `not_ready` means the objective is not somewhere a second pass can
 * start from.
 */
export const startReresearch = (objectiveId: string, note?: string) =>
  post<{ ok: true; live: boolean }>('/api/objectives/research-again', { objectiveId, note });

/**
 * The other way to ask again: more on one subject, rather than what is missing (F-109).
 *
 * **Deliberately a different call at a different price.** `startReresearch` above is the
 * plan-level question — "something is not in here" — and re-outlines the objective against
 * what it already has. This one skips the outline entirely and submits a single batched
 * request against one subject, told what has already been written about it so it goes after
 * what a first pass skipped. A quarter of the plan-level budget and a tenth of a first run.
 *
 * One endpoint with a `subjectId` parameter would have been fewer lines and would have made
 * "which of these am I about to be charged for" a question about a request body.
 */
export const startSubjectResearch = (objectiveId: string, subjectId: string) =>
  post<{ ok: true; live: boolean }>('/api/objectives/research-subject', { objectiveId, subjectId });

/**
 * Asks for the curriculum to be built from whatever has been read so far.
 *
 * Valid from `review`, and now from `active` as well — where it *extends* the plan
 * instead of replacing it, so the topics already being learned keep their ids and
 * with them the learner's review history. That is what makes closing a gap on a
 * running objective possible at all (F-87).
 *
 * `no_material` is a different failure from `not_ready` and is kept apart
 * deliberately: one means the objective has not got there yet, the other means it
 * has and there is nothing in the content store to build from — which is a thing
 * the learner can act on by adding a page of their own. `already_running` is the
 * third: a pass is in flight, which is a reason to wait rather than to look at
 * anything.
 *
 * **The `status` in the answer is not read, and must not be.** The route reports
 * `synthesising` whatever it started, and on a running objective that is exactly
 * the status the pass must never write — reading it back would put a screen into a
 * "building" state for an objective that is, correctly, still `active`. The
 * objective row is the only place progress is read from: `status` for a
 * pre-activation build, `synthesising` for a live one.
 */
export const startSynthesis = (objectiveId: string) =>
  post<{ ok: true }>('/api/objectives/synthesise', { objectiveId });

/**
 * Starts writing the questions for the curriculum.
 *
 * Unlike research and synthesis this leaves the status alone, so there is no
 * `status` in the answer to read progress from. A Message Batch is minutes at best
 * and up to a day by contract, and an in-progress status held that long would lock
 * the objective out of everything else — so `itemVersion` appearing on the
 * objective is what says it finished.
 */
export const startItemGeneration = (objectiveId: string) =>
  post<{ ok: true }>('/api/objectives/items', { objectiveId });

/**
 * "Start" — the word that turns a plan into something that will interrupt you.
 *
 * Deliberately its own act rather than the end of building the curriculum. It is
 * also where the learner's memory begins to exist: every item they will be asked
 * gets a review state here, which is what the due sweep looks for.
 */
export const activateObjective = (objectiveId: string) =>
  post<{ ok: true; items: number; nodes: number }>('/api/objectives/activate', { objectiveId });

export interface ResearchDocument {
  key: string;
  title: string;
  language: string;
  producedAt: string;
  origin: string;
  body: string;
  truncated: boolean;
  characters: number;
}

/**
 * What research found, so it can be read before a curriculum is built from it.
 *
 * Not part of `listSources`: a research pass creates no source row, because nothing
 * uploaded it. The two arrive at the same content store by different routes and are
 * read by different calls.
 */
export const getResearch = (objectiveId: string) =>
  get<{ documents: ResearchDocument[] }>(
    `/api/research?objectiveId=${encodeURIComponent(objectiveId)}`,
  );

/**
 * How far research has got, subject by subject (F-109).
 *
 * The one route in this file written to be polled, and it is one DynamoDB query behind the
 * scenes for exactly that reason. `run` is absent — not an error — for an objective nobody has
 * researched yet, because "you have not asked for this" and "this is not yours" are different
 * facts and the screen shows different things for them.
 *
 * `overall` is computed on the server. A percentage derived here from state names written by
 * the pipeline would be a second implementation of one rule, and the first time a state was
 * added this one would silently treat it as zero.
 */
export const getResearchProgress = (objectiveId: string) =>
  get<ResearchProgress | { run: undefined }>(
    `/api/research/progress?objectiveId=${encodeURIComponent(objectiveId)}`,
  );

/**
 * What happened to every subject, and which of them reached the plan (F-129).
 *
 * **The join between the two screens**, and the answer to the question that produced it: *"I
 * have 9 out of 12 researched and my plan is just 5 subjects."* Research produced subjects,
 * synthesis produced nodes, and until this route nothing related the two — so both numbers were
 * true, neither explained the other, and no screen could say which seven did not make it.
 *
 * **Not polled**, unlike `getResearchProgress` beside it. That one is a single query and is
 * refreshed every five seconds while an outline lands; this is three reads, one of them against
 * an index, and it is asked for on arrival and again when a run settles — which is when the
 * answer can have changed.
 *
 * The shape is `CoverageView`, declared in `plan/coverage.ts` rather than here, because the
 * module that decides what may be *said* about it has to be importable from a Node test and this
 * file reaches `fetch`.
 */
export const getCoverage = (objectiveId: string) =>
  get<CoverageView>(`/api/coverage?objectiveId=${encodeURIComponent(objectiveId)}`);

export interface ResearchSnippet {
  subjectId: string;
  title: string;
  /** How the pass described itself — `web_search:5 searches`. Absent if it did not say. */
  origin?: string;
  /** A paragraph of what was found. Absent when the document held nothing quotable. */
  snippet?: string;
}

/**
 * A line of what one subject's research actually says (F-149).
 *
 * **Asked for once per subject, as that subject settles**, and never on a timer. The research
 * a run pays for is twenty thousand characters a subject and none of it reached the learner
 * until a plan existed twenty minutes later; this is the smallest thing that puts some of it
 * on the screen while the rest is still arriving.
 *
 * A 404 is the ordinary answer and not a fault — a subject that has not produced a document
 * has nothing to quote — so the caller records that it asked and renders the subject without
 * a quotation, rather than complaining about a request the learner did not make.
 */
export const getResearchSnippet = (objectiveId: string, subjectId: string) =>
  get<ResearchSnippet>(
    `/api/research/snippet?objectiveId=${encodeURIComponent(objectiveId)}` +
      `&subjectId=${encodeURIComponent(subjectId)}`,
  );

// ---- Sources ----

export interface Source {
  sourceId: string;
  objectiveId: string;
  kind: string;
  title?: string;
  status: 'pending' | 'extracted' | 'failed';
  /** What a successful extraction produced. Absent on a failure. */
  detail?: string;
  /** Why it failed, in English, for the logs. Absent on a success. */
  error?: string;
  /** The same failure as a code this interface translates. */
  reason?: ExtractionFailure;
}

export interface ExtractedContent {
  title: string;
  language: string;
  method: string;
  producedAt: string;
  body: string;
  truncated: boolean;
}

async function get<T>(path: string): Promise<T | ApiError> {
  try {
    const response = await fetch(path, { credentials: 'same-origin' });
    const parsed = (await response.json()) as unknown;
    if (!response.ok) {
      return { error: (parsed as { error?: string }).error ?? 'request_failed', status: response.status };
    }
    return parsed as T;
  } catch {
    return { error: 'unreachable', status: 0 };
  }
}

export const listSources = (objectiveId: string) =>
  get<{ sources: Source[] }>(`/api/sources?objectiveId=${encodeURIComponent(objectiveId)}`);

/**
 * Read again, or take it away. A failure with neither is litter in somebody's own
 * list of material, which is what these two exist to stop being true.
 */
export const retrySource = (objectiveId: string, sourceId: string) =>
  post<{ ok: true }>('/api/sources/retry', { objectiveId, sourceId });

export const deleteSource = (objectiveId: string, sourceId: string) =>
  post<{ ok: true }>('/api/sources/delete', { objectiveId, sourceId });

export const readExtracted = (objectiveId: string, sourceId: string) =>
  get<{ content: ExtractedContent }>(
    `/api/sources/content?objectiveId=${encodeURIComponent(objectiveId)}&sourceId=${encodeURIComponent(sourceId)}`,
  );

export const presignUpload = (
  objectiveId: string,
  contentType: string,
  title: string,
  bytes: number,
) =>
  post<{ sourceId: string; url: string; key: string; maxBytes: number }>('/api/uploads', {
    objectiveId,
    contentType,
    title,
    bytes,
  });

// ---- The curriculum ----

/**
 * The whole node tree for one objective, already ordered by the API.
 *
 * Ordered *there*, not here: `gsi3` carries the order zero-padded so that a
 * lexicographic sort matches the learner's chosen order, and re-sorting the
 * result in the browser would be a second opinion about ordering that could
 * quietly disagree with the one the rest of the system uses.
 *
 * An empty `nodes` is the ordinary answer before anything has been built, not an
 * error — nothing is wrong with an objective that has no curriculum yet.
 */
export const getCurriculum = (objectiveId: string) =>
  get<{ nodes: PlanNode[]; curriculumVersion?: string }>(
    `/api/curriculum?objectiveId=${encodeURIComponent(objectiveId)}`,
  );

/**
 * Turning one subject on or off.
 *
 * One node per call rather than the whole selection, matching the endpoint: a
 * checkbox is one decision, and posting the entire set on every tap would make two
 * quick taps race to overwrite each other's *other* subjects, not just their own.
 *
 * The answer repeats `active` rather than being a bare `{ ok: true }`, and the
 * browser believes that over what it asked for — it is the only thing that knows
 * what is actually stored. `not_top_level` is a refusal by design: selection exists
 * only on top-level subjects, and a nested topic follows its parent.
 */
export const setNodeActive = (objectiveId: string, nodeId: string, active: boolean) =>
  post<{ ok: true; active: boolean }>('/api/curriculum/active', { objectiveId, nodeId, active });

// ---- Where the learner is ----

/**
 * What is known about a set of questions, in counts first and ratios second.
 *
 * Every ratio here arrives with its own denominator, and that is the whole contract
 * of this shape rather than a nicety: `recall` on its own is 100% for a learner who
 * has answered one question out of five hundred, and is true, and is a lie by
 * omission the moment it is rendered without `answered` beside it.
 */
export interface Evidence {
  /** Questions in scope. Explanations are excluded — nobody answers one. */
  questions: number;
  /** Never asked, so there is no evidence either way. Not the same as "got wrong". */
  untested: number;
  /** Asked, and either failed last time or forgotten at least once since. */
  struggling: number;
  /** Asked, and neither failed last time nor ever forgotten. */
  holding: number;
  /** The denominator of `recall`, and the only honest way to read it. */
  answered: number;
  due: number;
  /** Mean recall probability across answered items alone. Null when none are. */
  recall: number | null;
}

export interface SubjectProgress {
  nodeId: string;
  title: Record<string, string>;
  order: number;
  weight: number;
  /**
   * Whether the subject is still ticked on the plan.
   *
   * False does not mean it is being left alone: unticking after Start does not remove
   * the review states that were already written, so its questions keep arriving. The
   * panel says so rather than hiding the subject and disagreeing with the tutor.
   */
  active: boolean;
  /** Null when no questions were written for this subject, which is not zero. */
  mastery: number | null;
  evidence: Evidence;
}

/**
 * Today, in the terms the sending sweep actually decides in.
 *
 * **`dailyLimit` counts questions, and only questions.** Briefings and reminders are
 * sent outside it — a briefing because it teaches rather than asks, a reminder
 * because it repeats a question already asked and is bounded by the escalation
 * instead. So `sentToday` is a count of questions and the two never drift: anything
 * else arriving on the learner's phone is deliberately not in this shape.
 *
 * It is a ceiling and not a forecast, and since one question is outstanding at a time
 * it is a ceiling reality rarely reaches — which is why `answeredToday` is part of
 * this shape rather than something the screen infers.
 */
export interface Load {
  dailyLimit: number | null;
  /** Null when we do not know the learner's zone, so there is no local day to count. */
  sentToday: number | null;
  answeredToday: number;
  dueNow: number;
  awaitingAnswer: boolean;
  paused: boolean;
  pausedReason?: 'asked' | 'no_answer';
  timezone: string | null;
  localDay: string | null;
  reason: string;
  nextSendAt: string | null;
}

/**
 * Whether the objective is on track, when that question means anything.
 *
 * A score-targeted objective has no date and so cannot be behind one. The union
 * carries no verdict on that branch deliberately — answering "on track" for it would
 * be congratulating somebody for a deadline they never set.
 */
export type Pace =
  | { mode: 'score'; targetScore: number }
  | {
      mode: 'date';
      targetDate: string;
      /** False is trustworthy; true means only "not provably impossible". */
      feasible: boolean;
      requiredPerDay: number;
      capacityPerDay: number;
      daysLeft: number;
    };

export interface ProgressView {
  objectiveId: string;
  target: Objective['target'];
  standard: number;
  overall: Evidence;
  /** Weighted by what each subject is worth in the plan. Null when nothing measurable. */
  mastery: number | null;
  subjects: SubjectProgress[];
  explanations: number;
  unplaced: number;
  subjectsWithoutQuestions: number;
  load: Load;
  pace: Pace;
  /** True when the read stopped early, making every count above a floor. */
  partial: boolean;
  at: string;
}

/**
 * Where the learner is in one of their plans.
 *
 * **Optional objective id since F-151 step 6.** Progress used to be a statement about "the"
 * objective; it is a statement about one plan, and the caller may say which. Omitted, the API
 * answers for the plan the learner was most recently asked from — the same plan `getChat`
 * opens on, because both resolve it the same way, which is what stops this screen showing one
 * plan's percentage above another plan's conversation.
 *
 * `progress: null` is the ordinary answer when nothing is running — review state does
 * not exist before Start, and a plan awaiting approval is not a plan at nought per
 * cent. It is also the answer for an id that is not this learner's, deliberately.
 */
export const getProgress = (objectiveId?: string) =>
  get<{ progress: ProgressView | null }>(
    objectiveId
      ? `/api/progress?objectiveId=${encodeURIComponent(objectiveId)}`
      : '/api/progress',
  );

/**
 * The review sheet, fetched and handed to the browser as a file.
 *
 * Fetched rather than navigated to. Pointing the window at `/api/sheet` would be
 * three lines shorter and would render the refusals — "nothing answered yet", "the
 * rewrite did not come back" — as raw JSON in a blank tab, which is the API's error
 * shape shown to a person. Going through `fetch` keeps them as something the panel
 * can say in the learner's own language, and keeps the button's in-flight state
 * honest: a navigation reports nothing about whether the work finished.
 *
 * The filename comes from the response rather than being composed here, so the
 * server owns it in one place — and the object URL is revoked afterwards, because a
 * learner who downloads this every morning would otherwise accumulate a blob per
 * download for the life of the tab.
 */
export async function downloadSheet(objectiveId?: string): Promise<{ ok: true } | ApiError> {
  let response: Response;
  /*
   * Which plan's sheet (F-151 step 8). The route has taken one since step 6 and nothing was
   * passing it: on a tab that is not the default plan, the button downloaded the *other*
   * plan's review sheet — a PDF with a learner's own topics in it and nothing on the screen
   * to say which plan it came from. Absent still means the plan the API defaults to, which is
   * every learner with one plan.
   */
  const suffix = objectiveId ? `?objectiveId=${encodeURIComponent(objectiveId)}` : '';
  try {
    response = await fetch(`/api/sheet${suffix}`, { credentials: 'same-origin' });
  } catch {
    // Offline, or the API is unreachable. A thrown fetch and a refused request are
    // the same thing to somebody looking at a button that did nothing.
    return { error: 'unreachable', status: 0 };
  }

  if (!response.ok) {
    const failure = (await response.json().catch(() => ({}))) as { error?: string };
    return { error: failure.error ?? 'request_failed', status: response.status };
  }

  const blob = await response.blob();
  const disposition = response.headers.get('content-disposition') ?? '';
  const named = /filename="([^"]+)"/.exec(disposition);

  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = named?.[1] ?? 'nabu.pdf';
  document.body.append(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);

  return { ok: true };
}

// ---- Being reached ----

export interface WindowState {
  window: ReachWindow;
  /** Whether sending is stopped. See `setPaused`. */
  paused: boolean;
  /**
   * Why it stopped, when it has.
   *
   * `asked` is a setting the learner chose; `no_answer` is the escalation giving up
   * after three ignored reminders. The screen must say different things for each — one
   * is a decision, the other is something that happened to them — and a single
   * "paused" message for both would tell somebody who never touched it that they had.
   */
  pausedReason?: 'asked' | 'no_answer';
  /**
   * The learner's IANA zone, absent when we do not have one for them.
   *
   * Deliberately not filled in with a plausible default by the API: reachable hours
   * read in the wrong zone are worse than none, because `08:00`–`21:00` taken as UTC
   * is the middle of a Costa Rican night. The screen says so instead of showing
   * hours that will not be honoured.
   */
  timezone?: string;
  /** False until a window has actually been saved, which is when the defaults are labelled as such. */
  configured: boolean;
}

export const getWindow = () => get<WindowState>('/api/window');

/**
 * Stops or restarts the messages, without closing anything.
 *
 * Distinct from finishing an objective, which is the whole point: a busy week should
 * not cost a learner their progress. Everything is kept — the objective stays active
 * and every review state survives — and only the sending stops.
 */
export const setPaused = (paused: boolean) =>
  post<{ ok: true; paused: boolean }>('/api/pause', { paused });

// ---- Leaving ----

/** What the deletion destroyed, so the last thing the learner sees is a count. */
export interface DeletionReport {
  objectives: number;
  nodes: number;
  items: number;
  reviews: number;
  learnerRows: number;
  messages: number;
  pointers: number;
  /** The `EMAIL#` row that granted sign-in, if they had proved an address (F-101). */
  emailPointers: number;
  /** What we knew about mail to that address — bounces and the provider's words (F-106). */
  mailStatusRows: number;
  objectVersions: number;
  usageCountersKept: number;
}

/**
 * The literal word the API requires before it destroys anything.
 *
 * Not a password — the session is what authorises this. It is here so the one
 * irreversible endpoint in the system cannot be reached by a request that meant to
 * reach another one, and it is exported so the browser and the API agree on it in
 * one place rather than two string literals that could drift apart into a delete
 * button that silently stopped working.
 */
export const DELETE_CONFIRMATION = 'delete-everything';

/**
 * Erases the account and everything in it. There is no undo and no copy.
 *
 * The response clears the session cookie, so the browser is signed out by the same
 * request that destroyed the account — a signed JWT cannot be revoked, and a
 * session still presenting a deleted learner's id would let the next request create
 * an objective under it.
 */
export const deleteAccount = () =>
  post<{ ok: true; deleted: DeletionReport }>('/api/account/delete', {
    confirm: DELETE_CONFIRMATION,
  });

/**
 * Saves the whole window at once.
 *
 * All three settings together rather than one at a time, matching the endpoint: they
 * only mean something as a set, and two quick taps sending partial writes would
 * leave a schedule neither of them chose. A refusal comes back as `invalid_window`
 * with `reason` naming which part was wrong.
 */
export const saveWindow = (window: ReachWindow) =>
  post<{ ok: true; window: ReachWindow; timezone?: string }>('/api/window', window);

// ---- The conversation (F-100) ----

export interface ChatMessage {
  /** Unique within the thread, and stable. Used as the React key. */
  id: string;
  direction: 'in' | 'out';
  /**
   * When it happened, as the server stored it.
   *
   * **May not be a parseable instant.** A message that arrived without a readable
   * timestamp is filed under the literal word `unknown`, deliberately, so that a
   * redelivery lands on the same row. Anything rendering this has to survive that
   * rather than assuming a date — see `Chat.tsx`.
   */
  at: string;
  /** Marked as it was sent: a question, a briefing, or a reply to the learner. */
  text: string;
  purpose?: string;
  /**
   * Which transport carried it, where the writer knew.
   *
   * Three readings, and they are historical rather than a union to switch on: `whatsapp` on
   * the months of rows the retired sidecar delivered, `web` on a reply the API delivered by
   * writing the row, and **absent** on everything else — a row from before F-100, or one
   * whose producer honestly did not know which of a learner's devices a push would reach.
   * Nothing on this screen may require it to be present.
   */
  via?: string;
  /**
   * Which subject of the curriculum the message came out of, when the sender knew.
   *
   * An id, joined against `getProgress`'s `subjects` to get a title and a mastery —
   * see `Chat.tsx`. Present on questions and briefings, and on a reminder whose
   * question is in the same page; absent on the daily summary, on corrections, on
   * anything the learner typed, and on every row written before F-100.
   */
  nodeId?: string;
  /** The question it was about, when it was about one. */
  itemId?: string;
  /** The learner's message this answers, for the replies that answer one. */
  inReplyTo?: string;
  /**
   * The picture this question is about, when it is about one (F-46, shown by F-150).
   *
   * **The description and nothing else.** The key and the S3 version stay on the server:
   * they are what the presign is built from, and a browser that held them would be a
   * browser that could name an object to sign — which is the whole of what
   * `GET /api/chat/media` refuses to accept from a caller. The bytes are fetched
   * separately by `getMessageImage`, per message and on demand.
   *
   * Absent on every message but an image question, which today is most of them: an
   * objective whose learner has uploaded no photographs can have no image items at all.
   */
  media?: { alt: string };
}

export interface ChatPage {
  /** Oldest first, so the thread reads downwards. */
  messages: ChatMessage[];
  /**
   * Which plan's thread this is, as the API resolved it (F-151 step 6).
   *
   * The screen usually does not know: `objectiveId` is optional on the request and the
   * default is the API's to pick. This is what lets the chat tell "a notification about the
   * plan already on screen" from "a different conversation" — and getting that wrong throws
   * away the learner's loaded history and her place in it.
   *
   * Optional because an API deployed before this does not send it, and absent must read as
   * "this deploy does not say" rather than as a plan change.
   */
  objectiveId?: string;
  /** Where the older messages continue, when there are any. */
  olderCursor?: string;
  /**
   * How many messages this learner has not seen (F-120), for the icon badge.
   *
   * Absent on a history page, and the screen must treat absent as "do not touch the badge"
   * rather than as nought: a learner paging back through last week would otherwise clear a
   * count that is still true of the newest page they have not returned to.
   */
  unread?: number;
}

/**
 * A page of the thread, newest by default and older with a cursor.
 *
 * No learner id: the thread belongs to whoever the cookie says. The cursor walks
 * *backwards* through the history, which is why it is not called `next` — a screen
 * that read it as "later" would page into the future and never load its own past.
 */
/** What kind of landmark a message is. Mirrors `LandmarkKind` in `@nabu/shared`. */
export type LandmarkKind = 'briefing' | 'summary' | 'aid';

/**
 * One message worth jumping back to, as the sheet lists it (F-166).
 *
 * Leo, 2026-09-25: *"many times I want to go back to the information sent and after a couple
 * of days of questions and daily summaries it's really hard to spot it."*
 *
 * Deliberately not a `ChatMessage`. A line, a date, a kind and the id to scroll to — sending
 * whole briefings so the phone can show sixty characters of each would make the index cost
 * what reading the thread costs, which is the thing being avoided.
 */
export interface Landmark {
  /** The same id a `ChatMessage` has, so the thread can be scrolled to this bubble. */
  id: string;
  kind: LandmarkKind;
  /** As stored; may be the literal `unknown`, exactly like `ChatMessage.at`. */
  at: string;
  /** One line, marker already stripped and whitespace collapsed by the server. */
  preview: string;
  /** The subject, joined against `getProgress`'s `subjects` for a title. */
  nodeId?: string;
  itemId?: string;
}

export interface LandmarkIndex {
  /** Newest first — "the last briefing" is the first row. */
  landmarks: Landmark[];
  /** Which plan's, as the API resolved it. See `ChatPage.objectiveId`. */
  objectiveId?: string;
  /** Where the older landmarks continue, when there are any. */
  olderCursor?: string;
}

/**
 * The thread's landmarks, newest first.
 *
 * A *tap* and not a poll, which is why it is its own call and not a field on `getChat`: the
 * thread is re-read every twenty seconds and this list changes a few times a day.
 */
export const getLandmarks = (
  options: { objectiveId?: string; cursor?: string; limit?: number } = {},
) => {
  const query = new URLSearchParams();
  if (options.objectiveId) query.set('objectiveId', options.objectiveId);
  if (options.cursor) query.set('cursor', options.cursor);
  if (options.limit) query.set('limit', String(options.limit));
  const suffix = query.toString() ? `?${query.toString()}` : '';
  return get<LandmarkIndex>(`/api/chat/landmarks${suffix}`);
};

/**
 * Says how far the learner has read, so the icon badge can be right after the app is closed.
 *
 * Its own call rather than a side effect of `getChat`, because fetching the thread is not
 * reading it — the screen polls every twenty seconds while somebody is scrolled up in last
 * week's history, and marking those seen would clear a badge for messages nobody looked at.
 * The screen calls this only where it already decides the learner is at the newest message.
 *
 * The server refuses a marker that moves backwards, so a late response from a slow request
 * cannot make read messages unread again.
 */
export const markChatSeen = (messageId: string) =>
  post<{ ok: true }>('/api/chat/seen', { messageId });

/**
 * A signed, short-lived URL for the picture one message is about.
 *
 * Addressed by the *message* and not by the object, which is what makes it safe to expose:
 * the row lives under the learner's own partition, so the cookie decides what can be read
 * and there is no key, bucket or version a caller could substitute. The URL names the S3
 * `versionId` the question was written about, so a learner who uploads a better photograph
 * of the same page does not silently change what they were asked.
 *
 * Fails like anything else here and the caller treats a failure as "no picture": see
 * `Picture` in `chat/Picture.tsx`, where the description renders in its place.
 */
export const getMessageImage = (messageId: string) =>
  get<{ url: string; expiresIn: number }>(
    `/api/chat/media?messageId=${encodeURIComponent(messageId)}`,
  );

export const getChat = (
  options: { cursor?: string; limit?: number; objectiveId?: string } = {},
) => {
  const query = new URLSearchParams();
  if (options.cursor) query.set('cursor', options.cursor);
  if (options.limit) query.set('limit', String(options.limit));
  /*
   * Which plan's thread, when the screen knows (F-151 step 6). It knows when the learner
   * arrived from a notification, which now carries `?o=` — see `chatPathFor`. Omitted
   * otherwise, and the API answers for the plan they were most recently asked from.
   */
  if (options.objectiveId) query.set('objectiveId', options.objectiveId);
  const suffix = query.toString() ? `?${query.toString()}` : '';
  return get<ChatPage>(`/api/chat${suffix}`);
};

/**
 * Says something, and it is graded by the same code that graded every reply before it.
 *
 * **The id and the timestamp are generated by the caller and kept across a retry.**
 * They are what the server files the message under, and a second attempt that replays
 * both is refused as already handled instead of being marked a second time — two
 * model calls, two corrections, and a memory model advanced twice for one answer. A
 * server-minted id could not do that, because a failed request has no response to
 * remember.
 *
 * Nothing comes back but what happened to the message. The reply is read by the next
 * `getChat`, so there is one source for the thread and no way for what the learner
 * sees now to disagree with what a reload shows.
 */
export const sendChatMessage = (message: {
  text: string;
  messageId: string;
  sentAt: string;
  /**
   * Which plan this answer is about (F-151 step 6).
   *
   * **The attribution, and the reason it is worth sending.** `receiveFromWeb` filters the
   * outstanding questions to this plan's, so an answer typed after arriving from a
   * notification is graded against the question that notification was about rather than
   * against whichever plan was asked most recently. Omitted when the screen does not know,
   * which is the same default the thread was read with, so the two cannot disagree.
   */
  objectiveId?: string;
}) => post<{ ok: true; outcome: string }>('/api/chat', message);

/** One source behind a message, as the sheet shows it. */
export interface CitedSource {
  sourceId: string;
  /** `upload` is theirs, `link` is a page they gave us, `research` is a subject nabu read up on. */
  kind: 'upload' | 'link' | 'research';
  title?: string;
  /**
   * The page's address, and **only when there really is one**.
   *
   * Present on a `link` and absent on everything else. A research document is assembled
   * from several pages and has no single address, so the API refuses to invent one — see
   * `citations.ts`. Nothing here may fall back to showing `sourceId` as though it were a
   * location.
   */
  url?: string;
  /** How it got here, or what reading it produced — "web_search:12 searches". */
  detail?: string;
  /** Whether the learner's own upload could be read, when this is one. */
  status?: 'pending' | 'extracted' | 'failed';
}

/**
 * Where a message's material came from (F-116).
 *
 * Its own call rather than a field on `getChat`, because the thread is polled every twenty
 * seconds and this costs a DynamoDB query plus up to four S3 reads on the server. Fetched
 * when the sheet opens and not before — nobody pays for a panel they did not open.
 */
export const getMessageSources = (nodeId: string, itemId: string) =>
  get<{ sources: CitedSource[]; cited: number }>(
    `/api/chat/sources?nodeId=${encodeURIComponent(nodeId)}&itemId=${encodeURIComponent(itemId)}`,
  );
// ---- Notifications (F-102) ----

export interface PushState {
  /**
   * Whether the service has a VAPID key pair at all.
   *
   * A separate field rather than the absence of `publicKey`, because the two are
   * different things to say: "notifications are not set up on this service yet" is a
   * state the screen explains and the learner can do nothing about, and it must not
   * render as an error. The route answers 200 either way for the same reason
   * `/api/auth/me` answers 200 with `signedIn: false`.
   */
  configured: boolean;
  publicKey?: string;
  /**
   * How many browsers this learner has registered, across every device.
   *
   * Not the same question as "is this browser subscribed", which only the browser can
   * answer. Both are shown, because the state where they disagree — this browser thinks
   * it is subscribed and the server has pruned the row — is exactly the one in which a
   * learner is told notifications are on and receives none.
   */
  devices: number;
}

export const getPushState = async (): Promise<PushState | ApiError> => {
  try {
    const response = await fetch('/api/push', { credentials: 'same-origin' });
    const parsed = (await response.json()) as PushState & { error?: string };
    if (!response.ok) return { error: parsed.error ?? 'request_failed', status: response.status };
    return parsed;
  } catch {
    // Offline, or the API is down. Reported as an error rather than as "not configured":
    // telling a learner notifications are unavailable on this service because their train
    // went into a tunnel is a lie they would act on by not trying again.
    return { error: 'offline', status: 0 };
  }
};

export const subscribePush = (subscription: {
  endpoint: string;
  keys: { p256dh: string; auth: string };
  locale: string;
  /**
   * Whether this browser managed to report its own permission grant a moment ago (F-123).
   *
   * **This field is how the install funnel tells anybody that it is broken.** Every count in
   * the funnel is made by this page posting to `/api/install/funnel`, and a page cannot
   * report that its own reporting failed — if that route is renamed, blocked by an
   * extension, or answering 404 after a bad deploy, the funnel goes quiet and quiet is
   * indistinguishable from nobody installing the app. Which is the exact shape of every
   * fault this project keeps having: a component reporting success while doing nothing.
   *
   * So the bad news travels on a *different* request that is known to be working, because
   * the learner is subscribing on it right now. `false` here means a real person really
   * subscribed at a moment when the counting was broken, and the API turns it into the log
   * line behind `nabu-install-funnel-rejected`. No threshold, no guessing at traffic levels,
   * and no alarm that cries wolf on a quiet week.
   *
   * Absent means an older page that predates F-123, which is not a fault and is not counted
   * as one.
   */
  funnelCounted?: boolean;
}) => post<{ ok: true; devices: number }>('/api/push/subscribe', subscription);

/**
 * Counts one step of the install funnel, and answers whether the count was actually made.
 *
 * Its own function rather than `post`, for three reasons that are all about this being a
 * measurement and not a feature:
 *
 * - **It must never fail anything.** It is called from a page load and from the middle of
 *   turning notifications on. `post` throws on a network failure; this cannot, because a
 *   visitor whose count did not land must still get the product.
 * - **It must not hang.** It sits in front of `pushManager.subscribe` in `enablePush`, so a
 *   request left open by a captive portal would leave a learner watching a spinner while a
 *   metric decides what to do. Four seconds is far longer than the round trip and far
 *   shorter than the patience of somebody who has just tapped a switch.
 * - **It answers honestly.** `false` is returned only for a request that did not reach the
 *   API or was refused by it — never for a count this browser had already made. See
 *   `funnelCounted` above for what the difference is used for.
 *
 * The body carries two enum words and nothing else: no id, no session, no timestamp. The
 * route is deliberately unauthenticated, because installing happens before anybody has an
 * account — a learner reachable only by push needs the app on their home screen first.
 */
export const countInstallStep = async (stage: string, route: string): Promise<boolean> => {
  try {
    const response = await fetch('/api/install/funnel', {
      method: 'POST',
      // No cookie is needed and none is wanted: nothing about this count is about a person.
      credentials: 'omit',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ stage, route }),
      signal: AbortSignal.timeout(4000),
      /*
       * So a count made on the last render before a navigation still leaves. Without it the
       * browser cancels an in-flight request when the page goes away, which would lose
       * precisely the offers that were shown to somebody who immediately tapped install.
       */
      keepalive: true,
    });
    return response.ok;
  } catch {
    return false;
  }
};

export const unsubscribePush = (endpoint: string) =>
  post<{ ok: true }>('/api/push/unsubscribe', { endpoint });

/**
 * Sends the learner a message the way nabu reaches them.
 *
 * Deliberately not "encrypt me a notification here". It goes through the same queue and the
 * same dispatcher every real message goes through — same FIFO group, same device lookup,
 * same pruning of a dead subscription — so what arrives is the honest answer to "will nabu
 * reach me" rather than a demonstration that one code path works.
 *
 * A learner with no subscribed device is refused before anything is enqueued and told so,
 * which since F-103 is the only way to be unreachable: there is no longer a number to fall
 * back to.
 */
export const sendTestMessage = () => post<{ ok: true }>('/api/push/test', {});
